import {
  B6,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  Dropdown,
  H4,
  H5,
  Inline,
  Input,
  P,
  Radio,
  Spinner,
  Stack
} from "@adfenix/alexandria";
import { useEffect, useState } from "react";

import { Col } from "react-grid-system";
import Divider from "components/Divider";
import { Empty } from "components/Empty";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";
import { Modal } from "components/Modal";
import ResultsNoData from "components/NoData";
import { Table } from "components/Datatable";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getOfficesByHqId } from "api/services/configService";
import { getHqUsers } from "api/services/readLayerService";
import { ProgramService } from "@adfenix/types";
import useUserInfo from "hooks/useUserInfo";
import { useDisabledFields } from "./useDisabledFields";
import { useTranslation } from "react-i18next";

export const ProgramDetailsOverview = () => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { register, watch, setValue } =
    useFormContext<Partial<ProgramService.ProgramModel>>();
  const [showModal, setShowModal] = useState(false);
  const selectedOfficeIds = watch("selectedOfficeIds");

  const { userId, accessLevel } = useUserInfo();
  const [showOffices, setShowOffices] = useState(false);

  const { officeMode, ownedByUserId } = watch();
  const { hqId } = useParams<HqIdRouteParams>();

  const { data, isLoading } = useQuery(["getOfficesByHqId", hqId], () =>
    getOfficesByHqId(hqId!)
  );
  const { data: users, isInitialLoading: isLoadingUsers } = useQuery(
    ["getHqUsers", hqId],
    () => getHqUsers(hqId!),
    {
      enabled: accessLevel === "Multi-Brand"
    }
  );

  const selectedUser =
    users?.documents.find(x => x.id === ownedByUserId)?.email ?? "None";

  const offices = selectedOfficeIds
    ? selectedOfficeIds.map(id => {
        const office = data ? data.find(office => office.id === id) : null;

        if (office)
          return {
            name: office.name,
            id: id
          };

        return { name: id, id };
      })
    : [];

  useEffect(() => {
    if (!ownedByUserId && accessLevel !== "Multi-Brand" && userId) {
      setValue("ownedByUserId", userId);
    }
  }, [ownedByUserId, accessLevel, setValue, userId]);

  if (isLoading || isLoadingUsers) return <Spinner />;

  let userOptions =
    users && users.documents ? users.documents.map(x => x.email) : [];

  userOptions = ["None", ...userOptions];

  return (
    <Card>
      {showModal && (
        <Modal show={true} onClose={() => setShowModal(false)}>
          <Modal.Card style={{ maxWidth: "800px", width: "100%" }}>
            <Modal.Card.Head onClose={() => setShowModal(false)}>
              <Modal.Card.Title>
                {t("programBuilder.program.details.selectOffice.table.header")}
              </Modal.Card.Title>
            </Modal.Card.Head>
            <Modal.Card.Body>
              <Box pY="m" display="flex" flexDirection="column">
                <Table
                  columns={[
                    {
                      name: t(
                        "programBuilder.program.details.selectOffice.table.name.label"
                      ),
                      selector: "name"
                    }
                  ]}
                  selectableRows
                  selectableRowSelected={row =>
                    selectedOfficeIds?.some(officeId => officeId === row.id) ??
                    false
                  }
                  dense
                  selectableRowsHighlight
                  onSelectedRowsChange={selectedRowState => {
                    setValue(
                      "selectedOfficeIds",
                      selectedRowState.selectedRows.map(x => x.id),
                      {
                        shouldDirty: true
                      }
                    );
                  }}
                  data={data ?? []}
                  noDataComponent={<ResultsNoData />}
                  paginationPerPage={250}
                  paginationRowsPerPageOptions={[
                    25, 50, 75, 100, 150, 250, 500, 1000
                  ]}
                />
              </Box>
            </Modal.Card.Body>
            <Modal.Card.Foot>
              <ButtonGroup>
                <Button
                  logLabel="cancel-select-office-button"
                  onClick={() => {
                    setShowModal(false);
                  }}
                >
                  {t(
                    "programBuilder.program.details.selectOffice.table.button.done"
                  )}
                </Button>
              </ButtonGroup>
            </Modal.Card.Foot>
          </Modal.Card>
        </Modal>
      )}

      <H4>{t("programBuilder.program.details.overview.header")}</H4>

      <Stack spacing="m" style={{ paddingTop: "16px" }}>
        <Inline gap="m">
          <Box style={{ flexGrow: 1.2, maxWidth: "500px" }}>
            <Input
              label={t("programBuilder.program.details.overview.name.label")}
              {...register("name")}
              readOnly={disabledFields("name")}
            />
          </Box>

          <Input
            {...register("subjectType")}
            disabled
            label={t(
              "programBuilder.program.details.overview.subjectType.label"
            )}
          />
        </Inline>

        {accessLevel === "Multi-Brand" && (
          <Box display="flex">
            <Dropdown
              items={userOptions}
              defaultValue={selectedUser}
              onChange={item => {
                const value =
                  item === "None"
                    ? undefined
                    : users?.documents.find(x => x.email === item)?.id;

                setValue("ownedByUserId", value, {
                  shouldDirty: true
                });
              }}
              label={t("programBuilder.program.details.overview.owner.label")}
              disabled={disabledFields("ownedByUserId")}
            />
          </Box>
        )}
      </Stack>
      <Divider />

      <Box display="flex">
        <Col>
          <H5>
            {t("programBuilder.program.details.selectOffice.officeMode.header")}
          </H5>

          <Stack spacing="xs" pY="xs">
            <Radio
              {...register("officeMode")}
              value="ActiveOnAllOfficesExceptSelected"
              id="ActiveOnAllOfficesExceptSelected"
              readOnly={disabledFields("officeMode")}
              label={t(
                "programBuilder.program.details.selectOffice.officeMode.all.label"
              )}
            />

            <Radio
              {...register("officeMode")}
              value="ActiveOnSelectedOffices"
              id="ActiveOnSelectedOffices"
              readOnly={disabledFields("officeMode")}
              label={t(
                "programBuilder.program.details.selectOffice.officeMode.some.label"
              )}
            />
          </Stack>
        </Col>

        <Col>
          <Box display="flex" alignItems="center">
            <Badge
              lightness="darker"
              color={
                officeMode === "ActiveOnAllOfficesExceptSelected"
                  ? "danger"
                  : "success"
              }
            >
              {officeMode === "ActiveOnAllOfficesExceptSelected"
                ? t(
                    "programBuilder.program.details.selectOffice.officeMode.excludedOffices.label"
                  )
                : t(
                    "programBuilder.program.details.selectOffice.officeMode.includedOffices.label"
                  )}
            </Badge>

            <Box pX="s">
              <Button
                logLabel="add-office-button"
                variant="text"
                onClick={() => setShowModal(true)}
                disabled={disabledFields("selectedOffices")}
              >
                {t(
                  "programBuilder.program.details.selectOffice.officeMode.button.add"
                )}
                {officeMode === "ActiveOnAllOfficesExceptSelected"
                  ? "exclude"
                  : "include"}
              </Button>
            </Box>
          </Box>

          <Divider />

          {offices.length === 0 && (
            <Empty
              message={`${t(
                "programBuilder.program.details.selectOffice.officeMode.noOffices"
              )} ${
                officeMode === "ActiveOnAllOfficesExceptSelected"
                  ? t(
                      "programBuilder.program.details.selectOffice.officeMode.excluded"
                    )
                  : t(
                      "programBuilder.program.details.selectOffice.officeMode.included"
                    )
              }
          `}
            />
          )}

          {showOffices || offices.length <= 5 ? (
            offices
              .sort(function (a, b) {
                if (a.name < b.name) {
                  return -1;
                }
                if (a.name > b.name) {
                  return 1;
                }
                return 0;
              })
              .map(office => (
                <P style={{ fontWeight: 600 }} key={office.id}>
                  {office.name}
                </P>
              ))
          ) : (
            <Box display="flex" alignItems="center">
              <Box style={{ marginRight: 24 }}>
                <B6>
                  {offices.length}{" "}
                  {officeMode === "ActiveOnAllOfficesExceptSelected"
                    ? t(
                        "programBuilder.program.details.selectOffice.officeMode.excluded"
                      )
                    : t(
                        "programBuilder.program.details.selectOffice.officeMode.included"
                      )}
                </B6>
              </Box>

              <Button
                logLabel="see-all-offices-button"
                variant="text"
                onClick={() => setShowOffices(true)}
              >
                <i className={"uil uil-eye"} />
                {t(
                  "programBuilder.program.details.selectOffice.officeMode.button.seeAll"
                )}
              </Button>
            </Box>
          )}
        </Col>
      </Box>
    </Card>
  );
};
